import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="pt-24 pb-20 text-center">
        <p className="text-foreground-500">文章不存在</p>
        <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">返回首页</Link>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    url: `https://anzai-rara.com/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: '安斋拉拉主题站',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://anzai-rara.com/article/${article.slug}`}
        ogType="article"
        articlePublishedTime={article.date}
        articleAuthor={article.author}
        articleTags={article.tags}
      />

      <div className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <div className="max-w-3xl mx-auto">
            <span className="inline-block px-3 py-1 bg-primary-500 text-background-50 text-xs rounded-full font-medium mb-4">
              {article.categoryName}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
              {article.title}
            </h1>
          </div>
        </div>
      </div>

      <Breadcrumb
        items={[
          { name: '首页', path: '/' },
          { name: article.categoryName, path: `/category/${article.category}` },
          { name: article.title },
        ]}
      />

      <article className="w-full py-12 md:py-16 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="flex flex-wrap items-center gap-4 mb-8 pb-8 border-b border-background-200">
            <div className="flex items-center gap-3 text-sm text-foreground-500">
              <i className="ri-user-line"></i>
              <span>{article.author}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-foreground-500">
              <i className="ri-calendar-line"></i>
              <span>{article.date}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-foreground-500">
              <i className="ri-time-line"></i>
              <span>阅读时间 {article.readTime}</span>
            </div>
          </div>

          <div
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:mt-10 prose-headings:mb-4
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:font-bold
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
              prose-strong:text-foreground-900 prose-strong:font-semibold"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i> 返回{article.categoryName}
              </Link>
              <div className="flex items-center gap-3">
                <span className="text-xs text-foreground-400">分享：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer">
                  <i className="ri-weibo-line text-sm"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer">
                  <i className="ri-wechat-line text-sm"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer">
                  <i className="ri-link text-sm"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="mt-12 p-6 bg-background-100 rounded-lg">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">相关推荐</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {articles
                .filter((a) => a.slug !== article.slug && a.category === article.category)
                .slice(0, 2)
                .map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group flex gap-3 p-3 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-1">{related.date}</p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </article>
    </>
  );
}